import OrderAddressText from "../UI/OrderAddressText";
import { useOrderRowLayout } from "../orderRowLayout";
import { sellerTableHeader, sellerTableColors } from "./sellerTable";
import styled from "styled-components";
import { Order_detail } from "../Interface/Order";
import { CenterView, RowView2 } from "./common_style";

interface TableStyle {
  $width?: number | null;
  $height?: number | null;
}

export const TableHeader = styled(RowView2) <TableStyle>`
  min-width: ${(props) => (props.$width ? `${props.$width}rem` : "140rem")};
  ${sellerTableHeader}
  border-top: 1px solid ${sellerTableColors.border};
  span {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 100%;
    border: 1px solid ${sellerTableColors.border};
  }
`;
export const Table = styled(RowView2) <TableStyle>`
  min-width: ${(props) => (props.$width ? `${props.$width}rem` : "140rem")};
  height: ${(props) =>
    props.$height ? `${props.$height * 2.5}rem` : "2.5rem"};
  &.x2 {
    background-color: var(--admin-theme-raised, #f8f8f8);
  }
  span {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 100%;
    border-bottom: 1px solid ${sellerTableColors.border};
    border-left: 1px solid ${sellerTableColors.border};
    overflow: hidden;
  }
  img {
    margin-left: 3px;
    cursor: pointer;
  }
`;
export const HeaderRow = styled(CenterView) <TableStyle>`
  flex: 1;
  max-width: ${(props) => (props.$width ? `${props.$width}rem` : "auto")};
  height: 100%;
  word-break: keep-all;
  text-align: center;
  border-bottom: 1px solid ${sellerTableColors.border};
  border-right: 1px solid ${sellerTableColors.border};
  overflow: hidden;
  &.flex3 {
    flex: 2;
  }
`;
export const Row = styled(CenterView) <TableStyle>`
  height: 100%;
  flex: 1;
  max-width: ${(props) => (props.$width ? `${props.$width}rem` : "auto")};
  word-break: keep-all;
  text-align: center;
  border-bottom: 1px solid ${sellerTableColors.border};
  border-left: 1px solid ${sellerTableColors.border};
  overflow: hidden;
  &.flex3 {
    flex: 2;
  }
  &.last {
    border-right: 1px solid ${sellerTableColors.border};
    flex: 2;
  }
`;
export const MultiRows = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  div {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid ${sellerTableColors.border};
  }
`;

const keyMap: Record<
  | "seq"
  | "seq_check"
  | "주문일시"
  | "주문상태"
  | "상품코드"
  | "상품번호"
  | "품목명"
  | "옵션"
  | "단가"
  | "수량"
  | "총 금액"
  | "상태변경일"
  | "운송장번호"
  | "송장번호"
  | "택배사"
  | "택배 상태"
  | "환불메모",
  (data: Order_detail) => string | number | null
> = {
  seq: (data) => data.detail_seq,
  seq_check: (data) => data.detail_seq,
  주문일시: (data) => data.detail_order_dt.slice(0, 16).replaceAll("-", "."),
  상품번호: (data) => data.product_seq,
  상품코드: (data) => data.product_num == null || String(data.product_num).trim() === "" ? "-" : data.product_num,
  품목명: (data) => data.product_name,
  옵션: (data) => data.option_name?.replace("&", "/") || "-",
  단가: (data) => data.product_price.toLocaleString("ko-KR"),
  수량: (data) => data.order_cnt,
  "총 금액": (data) => data.order_price.toLocaleString("ko-KR"),
  상태변경일: (data) =>
    data.status_update_dt?.slice(0, 16).replaceAll("-", ".") || "-",
  주문상태: (data) => data.order_status,
  "택배 상태": (data) => !data.delivery_info?.trim() || data.delivery_info.trim() === '-' ? '송장 미등록' : data.tracking_status || '조회 전',
  운송장번호: (data) => data.delivery_info,
  송장번호: (data) => data.delivery_info,
  택배사: (data) => data.delivery_company ?? null,
  환불메모: (data) => data.refund_memo,
};

const RenderMultiRows2 = ({
  detailData,
  searchkey,
  Component,
  renderCell,
}: {
  detailData: Order_detail[];
  searchkey: string;
  Component?: React.ComponentType<{ detail_seq: number }>;
  renderCell?: (detail_seq: number) => React.ReactNode;
}) => {
  const height = detailData.length;

  return (
    <MultiRows>
      {detailData.map((item, idx) => {
        const nonborder = height === idx + 1 ? { border: 0 } : {};
        const text =
          searchkey in keyMap
            ? keyMap[searchkey as keyof typeof keyMap](item)
            : "";

        return (
          <div key={idx} style={nonborder}>
            {searchkey !== "seq_check" && text}
            {Component && <Component detail_seq={item.detail_seq} />}
            {renderCell && renderCell(item.detail_seq)}
          </div>
        );
      })}
    </MultiRows>
  );
};
export default RenderMultiRows2;

// ────────────────────────────────────────────────────────────────────────
// Grid 기반 주문 테이블 전용 (2026-07-15) — Order_all/Order_cancel/Order_refound/
// Order_paydeil 전용. 위 Row/Table/MultiRows/RenderMultiRows2(flex 기반)는
// MatchingModal/PayDoneModal이 그대로 쓰고 있어 건드리지 않고 별도로 추가한다.
//
// 문제: 기존 flex 구조는 "행(주문건) 전체 높이 = 아이템수×2.5rem"을 고정해두고
// 각 컬럼이 그 안에서 균등 n등분했다 — 품목명처럼 텍스트가 2줄로 감기면 그 칸만
// 필요한 공간이 더 커지는데 옆 컬럼은 여전히 고정 높이라 줄이 어긋났다.
// 해결: 실제 CSS Grid로 바꿔서 "그 아이템행에서 가장 큰 셀 기준"으로 브라우저가
// 자동으로 행 높이를 맞추게 한다(minmax(2.5rem, auto) + grid-auto-flow: column).
// ────────────────────────────────────────────────────────────────────────

export const OrderTableHeader = styled.div<{
  $columns: string;
  $minWidth?: number | null;
}>`
  display: grid;
  grid-template-columns: ${(props) => props.$columns};
  min-width: ${(props) =>
    props.$minWidth ? `${props.$minWidth}rem` : "140rem"};
  ${sellerTableHeader}
  border-top: 1px solid ${sellerTableColors.border};
`;

export const OrderHeaderCell = styled(CenterView)`
  white-space: nowrap;
  word-break: keep-all;
  text-align: center;
  border-bottom: 1px solid ${sellerTableColors.border};
  border-left: 1px solid ${sellerTableColors.border};
  overflow: hidden;
  &.last {
    border-right: 1px solid ${sellerTableColors.border};
  }
`;

export const OrderTable = styled.div<{
  $columns: string;
  $minWidth?: number | null;
  $height?: number | null;
}>`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: ${(props) => props.$columns};
  grid-template-rows: repeat(${(props) => props.$height || 1}, minmax(2.5rem, auto));
  min-width: ${(props) =>
    props.$minWidth ? `${props.$minWidth}rem` : "140rem"};
  &.x2 {
    background-color: var(--admin-theme-raised, #f8f8f8);
  }
`;

export const OrderCell = styled(CenterView)`
  white-space: nowrap;
  min-width: 0;
  grid-row: 1 / -1;
  word-break: keep-all;
  text-align: center;
  border-bottom: 1px solid ${sellerTableColors.border};
  border-left: 1px solid ${sellerTableColors.border};
  overflow: hidden;
  padding: 0.2rem 0.3rem;
  box-sizing: border-box;
  &.product-name { justify-content: flex-start; text-align: left; padding-left: var(--space-3); padding-right: var(--space-3); }
  &.item {
    grid-row: auto;
  }
  &.last {
    border-right: 1px solid ${sellerTableColors.border};
  }
`;

export const RenderOrderCells = ({
  detailData,
  searchkey,
  Component,
  renderCell,
  className,
  cellStyle,
}: {
  detailData: Order_detail[];
  searchkey: string;
  Component?: React.ComponentType<{ detail_seq: number }>;
  renderCell?: (detail_seq: number) => React.ReactNode;
  className?: string;
  cellStyle?: React.CSSProperties;
}) => {
  const layout = useOrderRowLayout();
  const height = detailData.length;

  return (
    <>
      {detailData.map((item, idx) => {
        const isLast = idx === height - 1;
        const text =
          searchkey in keyMap
            ? keyMap[searchkey as keyof typeof keyMap](item)
            : "";

        return (
          <OrderCell
            key={item.detail_seq ?? idx}
            className={["item", searchkey === "품목명" ? "product-name" : "", className].filter(Boolean).join(" ")}
            style={layout ? { ...cellStyle, gridRow: layout.detailRows.get(item.detail_seq) } : isLast ? { ...cellStyle } : cellStyle}
          >
            {searchkey !== "seq_check" && text !== "" && <OrderAddressText address={String(text ?? "-")} />}
            {Component && <Component detail_seq={item.detail_seq} />}
            {renderCell && renderCell(item.detail_seq)}
          </OrderCell>
        );
      })}
    </>
  );
};
